import { FC, useState } from 'react';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Table } from '../../../components/table/index';
import { DividendHooks } from '../../../hooks/index';
import { useScreenSize } from '../../../hooks/use-screen-size.hook';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { DividendPayoutsDetailsModal } from './dividend-payouts-details-modal';
import { columnOrderMap, columnTranslationKey } from './dividends-ledger.types';
import { DividendsLedgerCard } from './dividends-ledger-card';
import { DividendsLedgerItem, PayoutGroup } from './dividends-ledger-item';
import { DividendsLedgerMobileDetail } from './dividends-ledger-mobile-detail';

const VISIBLE_GROUPS_LIMIT = 3;

const groupByPayout = (
  rows: ReturnType<typeof DividendHooks.useDividendStakeholderPayouts>['payouts'],
): PayoutGroup[] => {
  const map = new Map<string, PayoutGroup>();

  for (const row of rows) {
    const existing = map.get(row.id);
    if (existing) {
      existing.items.push(row);
      existing.totalAmount += row.itemAmount;
    } else {
      map.set(row.id, {
        frequency: row.frequency,
        id: row.id,
        items: [row],
        name: row.name,
        payoutDate: row.payoutDate,
        periodIndex: row.periodIndex,
        status: row.status,
        totalAmount: row.itemAmount,
        year: row.year,
      });
    }
  }

  return Array.from(map.values());
};

type Props = {
  enabled: boolean;
  stakeholderId: string;
  stakeholderName: string;
};

export const DividendsLedger: FC<Props> = ({ stakeholderId, stakeholderName, enabled }) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState<null | PayoutGroup>(null);

  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const company = selectedCompanyStorage(({ company }) => company);
  const currencySymbol = company?.currency?.symbol ?? '$';
  const { width } = useScreenSize();
  const isPhone = width < 460;

  const { payouts, isLoading } = DividendHooks.useDividendStakeholderPayouts({
    companyId,
    enabled,
    stakeholderId,
  });
  const { exportStakeholderPayouts, isExportingStakeholder } = DividendHooks.useDividendExport();

  if (isLoading || payouts.length === 0) return null;

  const groups = groupByPayout(payouts);
  const visibleGroups = groups.slice(0, VISIBLE_GROUPS_LIMIT);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 !pt-3 lg:p-[2px]">
      <div className="flex items-center justify-between pl-4 pr-2">
        <span className="text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="DIVIDEND_HISTORY" />
        </span>
        {!isPhone && (
          <button
            className="flex items-center gap-1 text-xs font-[550] text-brand-700 hover:underline"
            onClick={() => setIsModalOpen(true)}
            type="button"
          >
            <AppFormattedMessage id="VIEW_ALL" />
            <ChevronDownIcon className="h-3 w-3 -rotate-90" />
          </button>
        )}
      </div>

      {isPhone ? (
        <div className="flex flex-col gap-3">
          {visibleGroups.map((group) => (
            <DividendsLedgerCard
              currencySymbol={currencySymbol}
              group={group}
              key={group.id}
              onViewDetails={() => setDetailGroup(group)}
            />
          ))}
        </div>
      ) : (
        <div className="flex gap-2 overflow-hidden rounded-md bg-white px-2 pb-2 pt-1 shadow-sm">
          <div className="flex h-full w-full flex-col gap-1 overflow-x-auto rounded-md border border-gray-100">
            {/* The shared header is 12px; this table is specified at 14 (Desktop/text-sm), and the
                custom `text-label-md` outranks a plain `text-sm` in the generated CSS. */}
            <Table
              columnOrder={columnOrderMap}
              columnsTitle={columnTranslationKey}
              tHeadClassNames={{ labelClassName: '!text-sm' }}
            >
              {visibleGroups.map((group) => (
                <DividendsLedgerItem
                  currencySymbol={currencySymbol}
                  group={group}
                  isExpanded={expandedIds.has(group.id)}
                  key={group.id}
                  onToggle={() => toggleExpand(group.id)}
                />
              ))}
            </Table>
          </div>
        </div>
      )}

      <DividendPayoutsDetailsModal
        currencySymbol={currencySymbol}
        isExporting={isExportingStakeholder}
        onClose={() => setIsModalOpen(false)}
        onExport={() => exportStakeholderPayouts({ stakeholderId, stakeholderName })}
        open={isModalOpen}
        payouts={payouts}
        stakeholderName={stakeholderName}
      />

      {detailGroup && (
        <DividendsLedgerMobileDetail
          currencySymbol={currencySymbol}
          group={detailGroup}
          onBack={() => setDetailGroup(null)}
        />
      )}
    </div>
  );
};
